import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth }         from "@context/AuthContext";
import { useRole }         from "@hooks/useRole";
import ProfileHeader       from "@features/profile/view/ProfileHeader";
import ProfileGlance       from "@features/profile/view/ProfileGlance";
import ProfileTabs         from "@features/profile/view/ProfileTabs";
import SummaryTab          from "@features/profile/view/SummaryTab";
import ExperienceTab       from "@features/profile/view/ExperienceTab";
import EducationTab        from "@features/profile/view/EducationTab";
import SectionCard           from "@features/profile/view/SectionCard";
import RecruiterNotesTab     from "@features/profile/view/RecruiterNotesTab";
import ProposeForTab          from "@features/profile/view/ProposeForTab";
import SimilarTab             from "@features/profile/view/SimilarTab";
import RecommendHistoryTab    from "@features/profile/view/RecommendHistoryTab";
import ConnectIQTab           from "@features/profile/view/ConnectIQTab";
import axios               from "axios";
import RecommendModal      from "@features/profile/view/RecommendModal";
import { API_BASE, PROFILE_IMG_BASE } from "@config/env";
import { Modal, ModalFooter } from "@common/Modal";


const COLORS   = ["#2563EB","#16A34A","#F59E0B","#EC4899","#06B6D4","#7C3AED"];

const post = (ep, f) => {
  const fd = new FormData();
  Object.entries(f).forEach(([k,v]) => fd.append(k,v));
  return axios.post(`${API_BASE}${ep}`, fd).then(r=>r.data).catch(()=>null);
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const parseDate = (d) => {
  if (!d) return new Date(0);
  if (/^\d{2}-\d{2}-\d{4}/.test(d)) { const [mm,dd,yyyy]=d.split("-"); return new Date(`${yyyy}-${mm}-${dd}`); }
  return new Date(d);
};
const parseYr = (d) => {
  if (!d) return "";
  if (/^\d{2}-\d{2}-\d{4}/.test(d)) return d.split("-")[2];
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) return d.split("-")[0];
  try { return new Date(d).getFullYear().toString(); } catch { return ""; }
};
const fmtMonYr = (d) => {
  if (!d) return "";
  try {
    const dt = parseDate(d);
    if (isNaN(dt)) return parseYr(d);
    return `${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
  } catch { return parseYr(d); }
};
const fmtPeriod = (start, end, isCurrent) => {
  const s = fmtMonYr(start);
  const e = parseInt(isCurrent)===1 ? "Present" : fmtMonYr(end);
  return s || e ? `${s}${s&&e?" — ":""}${e}` : "";
};

// Map API response → candidate shape expected by existing UI components
function mapToCandidate(rd, color) {
  const pd  = rd.profile_details || {};
  const cd  = rd.contact_details || {};

  // Fields are at TOP LEVEL of rd (not nested in profile_details)
  const firstName = rd.first_name || pd.first_name || "";
  const lastName  = rd.last_name  || pd.last_name  || "";
  const name      = rd.profile_name || pd.profile_name
                  || `${firstName} ${lastName}`.trim() || "Unknown";

  const skills = (rd.skill_details || []).map(s => s.skill_name).filter(Boolean);
  const eduRaw = Array.isArray(rd.education_details) ? rd.education_details : [];
  const empRaw = Array.isArray(rd.employment_details) ? rd.employment_details : [];
  const certs  = Array.isArray(rd.certification_details) ? rd.certification_details : [];

  // language_details_V2 is the correct field
  const langs = (rd.language_details_V2 || rd.language_details || [])
    .map(l => l.value || l.language_name).filter(Boolean);

  // availability is in availability_details
  const avail = parseInt(rd.availability_details?.availability ?? pd.availability) || 0;

  const education = eduRaw.map(e => ({
    id:     e.education_history_id,
    degree: [e.degree_name, e.subject].filter(Boolean).join(" in "),
    school: e.school_name || "",
    logo:   "fa-graduation-cap",
    color:  "var(--fc-primary)",
    period: fmtMonYr(e.passed_on),
    grade:  "",
    desc:   "",
  }));

  const experience = empRaw
    .sort((a,b) => parseDate(b.start_date) - parseDate(a.start_date))
    .map(e => ({
      id:       e.employment_history_id,
      title:    e.job_title    || "",
      company:  e.employer_name|| "",
      logo:     "fa-briefcase",
      color:    "var(--fc-green)",
      period:     fmtPeriod(e.start_date, e.end_date, e.is_current),
      isCurrent:  parseInt(e.is_current) === 1,
      location:   [e.city, e.province].filter(Boolean).join(", "),
      desc:       e.description || "",
      tags:       [],
    }));

  return {
    id:           rd.user_slug || pd.user_slug,
    resource_id:  rd.user_id  || pd.user_id,
    user_slug:    rd.user_slug || pd.user_slug,
    name,
    role:         rd.user_profile_role || pd.user_profile_role || "",
    company:      rd.firm_name || pd.firm_name || "",
    firmLogo:     rd.firm_logo || pd.firm_logo || "",
    location:     [cd.city_name, cd.state_name, cd.country_name].filter(Boolean).join(", "),
    email:        (cd.user_email && cd.user_email!=="Not available") ? cd.user_email : "",
    phone:        (cd.mobile     && cd.mobile!=="Not available")     ? cd.mobile     : "",
    availability: avail,
    status:       avail >= 30 ? "Available" : avail >= 10 ? "Partially Available" : "Busy",
    bio:          rd.profile_bio || pd.profile_bio || "",
    initials:     name.replace(/\s*\(.*?\)/g,"").split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase() || "?",
    color,
    skills,
    skillCount:     parseInt(rd.skill_count?.skill_count) || skills.length,
    certifications: certs.map(cert => ({
      id:    cert.certification_id,
      title: cert.certification_title,
    })),
    languages:    langs,
    education,
    experience,
    matchingJobs: [],
    tags:         [],
    exp:          "",
    salary:       "",
    isFav:        rd.isFav ?? pd.isFav ?? 2,  // 1=starred, 2=not starred
    is_advertised: rd.is_advertised || pd.is_advertised,
    firm_id:       rd.firm_id || pd.firm_id,
    resource_manager_id: rd.resource_manager_id || pd.resource_manager_id,
    profile_image_path:  rd.profile_image_path || pd.profile_image_path,
    rm_details: rd.rm_details || {},
    documents:  rd.document_details || [],
  };
}


// Shimmer skeleton pulse
const Pulse = ({ w="100%", h=12, r=6, mb=0 }) => (
  <div style={{ width:w, height:h, borderRadius:r, background:"linear-gradient(90deg,var(--fc-border-light) 25%,var(--fc-border) 50%,var(--fc-border-light) 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite", marginBottom:mb }} />
);

function UpdateViaResumeModal({ userSlug, onClose, onUpdated }) {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const upload = async () => {
    if (!file) { setError("Please select a resume file"); return; }
    setUploading(true); setError(""); setProgress(0);
    const fd = new FormData();
    fd.append("user_slug", userSlug);
    fd.append("file", file);
    try {
      const res = await axios.post(`${API_BASE}update-profile-from-resume`, fd, {
        onUploadProgress: e => setProgress(Math.round((e.loaded * 100) / e.total)),
      });
      if (res.data?.status === 1) {
        onUpdated();
      } else {
        setError(res.data?.message || "Unable to update profile from resume.");
        setUploading(false);
      }
    } catch {
      setError("Network error. Please try again.");
      setUploading(false);
    }
  };

  return (
    <Modal title="Update Profile via Resume" onClose={!uploading ? onClose : undefined} maxWidth={460}
      footer={
        <ModalFooter onCancel={onClose} onConfirm={upload}
          confirmDisabled={!file} confirmBusy={uploading}
          confirmLabel={uploading ? "Uploading..." : "Update Profile"} />
      }>
      {error && (
        <div style={{ padding:"10px 12px", background:"var(--fc-red-light)", border:"1px solid var(--fc-red-border)", borderRadius:9, marginBottom:14, fontSize:13, color:"var(--fc-red)" }}>
          <i className="fa-solid fa-circle-exclamation" style={{marginRight:7}} />{error}
        </div>
      )}
      <p style={{ fontSize:13, color:"var(--fc-text-secondary)", marginBottom:14 }}>
        Upload an updated resume and we'll refresh your skills, experience, and education from it.
      </p>
      <div
        onClick={() => !uploading && document.getElementById("resume-update-file-input")?.click()}
        style={{ border:"2px dashed var(--fc-border)", borderRadius:12, padding:"32px 20px", textAlign:"center", cursor:uploading?"wait":"pointer", transition:"all 0.15s",
          ...(file ? { borderColor:"var(--fc-primary)", background:"var(--fc-primary-light)" } : {}) }}
        onDragOver={e=>{e.preventDefault();e.currentTarget.style.borderColor="var(--fc-primary)";}}
        onDragLeave={e=>{e.currentTarget.style.borderColor=file?"var(--fc-primary)":"var(--fc-border)";}}
        onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f){setFile(f);setError("");}}}>
        <i className="fa-solid fa-file-arrow-up" style={{ fontSize:28, color:file?"var(--fc-primary)":"var(--fc-text-hint)", display:"block", marginBottom:10 }} />
        {file ? (
          <><div style={{ fontSize:14, fontWeight:700, color:"var(--fc-primary)" }}>{file.name}</div><div style={{ fontSize:12, color:"var(--fc-text-muted)", marginTop:4 }}>Click to change</div></>
        ) : (
          <><div style={{ fontSize:14, fontWeight:600, color:"var(--fc-text-secondary)" }}>Drop resume here or click to browse</div><div style={{ fontSize:12, color:"var(--fc-text-subtle)", marginTop:4 }}>PDF, DOC, DOCX supported</div></>
        )}
      </div>
      <input id="resume-update-file-input" type="file" accept=".pdf,.doc,.docx,.txt" style={{ display:"none" }}
        onChange={e=>{const f=e.target.files[0];if(f){setFile(f);setError("");}}} />

      {uploading && (
        <div style={{ marginTop:14 }}>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"var(--fc-text-muted)", marginBottom:5 }}>
            <span>Uploading & parsing...</span><span>{progress}%</span>
          </div>
          <div style={{ height:6, background:"var(--fc-border-light)", borderRadius:99, overflow:"hidden" }}>
            <div style={{ height:"100%", width:`${progress}%`, background:"var(--fc-primary)", borderRadius:99, transition:"width 0.3s" }} />
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function CandidateProfilePage() {
  const { id } = useParams();
  const navigate  = useNavigate();
  const { user, userSlug } = useAuth();

  const [candidate, setCandidate] = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState("summary");
  const [error,     setError]     = useState("");
  const [isAdv,     setIsAdv]     = useState("0");
  const [rd,        setRd]         = useState(null); // raw resource_details
  const [displayView,setDisplayView]= useState("default"); // resource_access from API
  const [clientPreview, setClientPreview] = useState(false); // local-only override for the "Client View" tab - doesn't touch the real resource_access from the API
  const [selfSvcIds,setSelfSvcIds]= useState(null);
  const [advBusy,   setAdvBusy]   = useState(false);
  const [selfBusy,  setSelfBusy]  = useState(false);
  const [showRecom, setShowRecom] = useState(false);
  const [showResumeUpdate, setShowResumeUpdate] = useState(false);
  const [toast,     setToast]     = useState(null);

  const { isFirmOwner, isRM, isResource, hasAnyRole, ROLES, isEnterpriseFirm } = useRole();
  const firmId   = user?.firm_details?.firm_id;

  const loadProfile = () => {
    if (!id || !userSlug) return;
    post("get-resource-details", { resource_slug: id, user_slug: userSlug })
      .then(data => {
        if (data?.status === 1 && data?.resource_details) {
          const rd2 = data.resource_details;
          setRd(rd2);
          setDisplayView(rd2.resource_access || "default");
          setCandidate(mapToCandidate(rd2, COLORS[0]));
          setIsAdv(String(rd2.is_advertised ?? rd2.profile_details?.is_advertised ?? '0'));
          setSelfSvcIds(rd2.firm_ids_self_services ?? rd2.profile_details?.firm_ids_self_services ?? null);
        } else {
          setError(data?.message || "Profile not found.");
        }
        setLoading(false);
      });
  };

  useEffect(() => { loadProfile(); }, [id, userSlug]);

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3000); };

  const checkSelf = (ids) => {
    if (!ids) return false;
    return ids.toString().split(", ").includes(String(firmId));
  };

  const [confirmToggle, setConfirmToggle] = useState(null); // "adv" | "self" | null

  const doUpdateAdv = async () => {
    setAdvBusy(true);
    const resourceSlug = rd?.user_slug || id;
    const res = await post("update-is-advertised", {
      user_slug:     userSlug,
      resource_slug: resourceSlug,
      is_advertised: parseInt(isAdv),
    });
    setAdvBusy(false);
    if (res && Number(res.status) === 1) {
      const next = isAdv === "0" ? "1" : "0";
      setIsAdv(next);
      showToast(next === "1" ? "Now visible to clients" : "Hidden from clients");
    } else showToast(res?.message || "Failed", "error");
  };

  const doUpdateSelf = async () => {
    setSelfBusy(true);
    const resourceSlug = rd?.user_slug || id;
    const res = await post("update-self-service", { user_slug: userSlug, resource_slug: resourceSlug });
    setSelfBusy(false);
    if (res && Number(res.status) === 1) {
      setSelfSvcIds(res.resource?.firm_ids_self_services ?? null);
      showToast("Self-service access updated!");
    } else showToast(res?.message || "Failed", "error");
  };

  if (loading) {
    return (
      <div>
        <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>

        {/* Profile header skeleton */}
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:16, padding:"22px 24px", marginBottom:12, boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
          <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
            <div style={{ width:72, height:72, borderRadius:16, background:"var(--fc-border-light)", flexShrink:0 }} />
            <div style={{ flex:1 }}>
              <Pulse w="45%" h={18} r={8} mb={10} />
              <Pulse w="30%" h={13} r={6} mb={8} />
              <Pulse w="50%" h={11} r={5} />
            </div>
          </div>
          <div style={{ display:"flex", gap:8, marginTop:18 }}>
            {[80,90,70].map((w,i) => <div key={i} style={{ height:30, width:w, borderRadius:99, background:"var(--fc-border-light)" }} />)}
          </div>
        </div>

        {/* Glance row skeleton */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:10, marginBottom:12 }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:14, padding:"16px 18px", boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
              <Pulse w="50%" h={10} r={4} mb={12} />
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {[1,2,3].map(j=><div key={j} style={{ height:22, width:50+j*10, borderRadius:99, background:"var(--fc-border-light)" }} />)}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs skeleton */}
        <div style={{ display:"flex", gap:6, marginBottom:14 }}>
          {[90,80,80,100,90].map((w,i) => (
            <div key={i} style={{ height:34, width:w, borderRadius:99, background:i===0?"var(--fc-border)":"var(--fc-border-light)" }} />
          ))}
        </div>

        {/* Tab content skeleton */}
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:14, padding:"22px 24px", boxShadow:"0 1px 4px rgba(0,0,0,0.04)" }}>
          <Pulse w="20%" h={13} r={5} mb={16} />
          <Pulse w="100%" h={11} r={4} mb={8} />
          <Pulse w="90%" h={11} r={4} mb={8} />
          <Pulse w="75%" h={11} r={4} mb={8} />
          <Pulse w="85%" h={11} r={4} mb={0} />
        </div>
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <SectionCard>
        <div className="text-center py-4">
          <p className="fw-bold mb-2">{error || "Profile not found"}</p>
          <button className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill" onClick={() => navigate(-1)}>Go back</button>
        </div>
      </SectionCard>
    );
  }

  const isRMOrOwner = hasAnyRole([ROLES.FIRM_OWNER, ROLES.RM]);

  // Same firm check (for Recruiter Notes / Opportunities / Similar Teammate)
  const sameFirm = isEnterpriseFirm && isRM &&
    (String(candidate?.firm_id) === String(firmId) ||
     String(rd?.rm_details?.resource_manager_id) === String(user?.user_id));

  // Build tabs matching old ProfileInfoTabSection.js logic exactly
  // effectiveDisplayView: when clientPreview is on, this locally overrides
  // the real displayView from the API - lets an RM preview exactly what a
  // client would see without needing a second API call or a separate page.
  const effectiveDisplayView = clientPreview ? "client" : displayView;
  const TABS = [
    { key:"summary",     label:"Summary",         icon:"fa-user",           show: true },
    { key:"experience",  label:"Experience",       icon:"fa-briefcase",      show: true },
    { key:"education",   label:"Education",        icon:"fa-graduation-cap", show: true },
    { key:"notes",       label:"Recruiter Notes",  icon:"fa-note-sticky",    show: effectiveDisplayView === "default" && sameFirm },
    { key:"proposeFor",  label:"Opportunities",    icon:"fa-bullseye",       show: effectiveDisplayView === "default" && sameFirm },
    { key:"similar",     label:"Similar Teammate", icon:"fa-users",          show: effectiveDisplayView === "default" && sameFirm },
    { key:"recommend",   label:"Recommendations",  icon:"fa-thumbs-up",      show: isRM && !clientPreview },
    { key:"connectiq",   label:"Connect IQ",       icon:"fa-robot",          show: isRM && !clientPreview,
      badge: null },
  ].filter(t => t.show);

  const isViewingSelf = String(id) === String(userSlug);
  const canEdit = isViewingSelf || isFirmOwner || (isRM &&
    (String(candidate.resource_manager_id) === String(user?.user_id) ||
     String(candidate.firm_id) === String(firmId) ||
     String(rd?.rm_details?.resource_manager_id) === String(user?.user_id)));

  return (
    <div>
      {/* Internal/Client view toggle - only for someone reviewing a candidate
          from the recruiter side (firm owner/RM, or an RM editing their own
          listing). A Resource viewing their own profile gets canEdit=true
          via isViewingSelf too, but "preview how a client sees me" makes no
          sense for them, so this one block is gated with the extra
          !isResource on top of canEdit - the Edit button below and the
          readOnly props elsewhere still use plain canEdit unchanged. */}
      {canEdit && !isResource && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, padding:"10px 16px", background: clientPreview ? "#EFF6FF" : "var(--fc-card-bg)", border: `1px solid ${clientPreview ? "#BFDBFE" : "var(--fc-border)"}`, borderRadius:12, marginBottom:14, flexWrap:"wrap" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <button onClick={() => { setClientPreview(v => !v); setTab("summary"); }} title="Toggle between internal and client view"
              style={{ width:40, height:22, borderRadius:99, background: clientPreview ? "#2563EB" : "var(--fc-text-hint)", border:"none", cursor:"pointer", position:"relative", transition:"background 0.25s", flexShrink:0, padding:0 }}>
              <span style={{ position:"absolute", top:3, left: clientPreview ? 20 : 3, width:16, height:16, borderRadius:"50%", background:"#fff", boxShadow:"0 1px 3px rgba(0,0,0,0.25)", transition:"left 0.25s" }} />
            </button>
            <span style={{ fontSize:13, fontWeight:600, color: clientPreview ? "#1E40AF" : "var(--fc-text-primary)" }}>
              {clientPreview ? "Client View" : "Internal View"}
            </span>
          </div>
          {clientPreview && (
            <span style={{ fontSize:12, color:"#1E40AF" }}>
              <i className="fa-solid fa-eye me-1" />Name and contact info are hidden below, matching what a client sees.
            </span>
          )}
        </div>
      )}

      <ProfileHeader candidate={candidate} readOnly={!canEdit} hideIdentity={clientPreview} />

      {/* ── Self-view action bar — Resource role viewing their OWN profile.
           Kept separate from the bar below (rather than broadening its
           conditions) so RM-only toggles never get exposed to a candidate. ── */}
      {rd && isResource && isViewingSelf && (
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:12, padding:"12px 18px", margin:"10px 0", display:"flex", justifyContent:"flex-end", gap:8, boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
          <button onClick={() => setShowResumeUpdate(true)} className="fc-btn fc-btn-sm fc-btn-secondary fc-btn-pill">
            <i className="fa-solid fa-file-arrow-up me-1" style={{fontSize:10}} />Update via Resume
          </button>
          <button onClick={() => navigate("/edit-profile")} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
            <i className="fa-solid fa-pen me-1" style={{fontSize:10}} />Edit Profile
          </button>
        </div>
      )}

      {/* ── Action bar — toggles + buttons ── */}
      {rd && (!isResource) && !clientPreview && (
        <div style={{ background:"var(--fc-card-bg)", border:"1px solid var(--fc-border)", borderRadius:12, padding:"12px 18px", margin:"10px 0", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
          {/* Left — toggles */}
          <div style={{ display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
            {/* Visible to Clients */}
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <button onClick={()=>setConfirmToggle("adv")} disabled={advBusy} title="Toggle visibility to clients"
                style={{ width:40, height:22, borderRadius:99, background:isAdv==="1"?"var(--fc-green)":"var(--fc-text-hint)", border:"none", cursor:"pointer", position:"relative", transition:"background 0.25s", flexShrink:0, padding:0 }}>
                <span style={{ position:"absolute", top:3, left:isAdv==="1"?20:3, width:16, height:16, borderRadius:"50%", background:"#fff", boxShadow:"0 1px 3px rgba(0,0,0,0.25)", transition:"left 0.25s" }} />
              </button>
              <span style={{ fontSize:12, color:"var(--fc-text-muted)", fontWeight:500 }}>Visible to Clients</span>
              {isAdv==="1" && <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:99, background:"var(--fc-green-light)", color:"var(--fc-green)", border:"1px solid var(--fc-green-border)" }}>Active</span>}
            </div>

            {/* Self-Service. Row visible to anyone but the
                resource itself (Owner/RM/Client); the actual toggle only
                renders for the specific RM who manages this resource -
                everyone else sees a read-only label. No firm-type
                restriction, matching the listing page's behavior. */}
            {!isResource && (
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                {String(rd?.rm_details?.resource_manager_id) === String(user?.user_id) ? (
                  <button onClick={()=>setConfirmToggle("self")} disabled={selfBusy} title="Toggle self-service access"
                    style={{ width:40, height:22, borderRadius:99, background:checkSelf(selfSvcIds)?"var(--fc-green)":"var(--fc-text-hint)", border:"none", cursor:"pointer", position:"relative", transition:"background 0.25s", flexShrink:0, padding:0 }}>
                    <span style={{ position:"absolute", top:3, left:checkSelf(selfSvcIds)?20:3, width:16, height:16, borderRadius:"50%", background:"#fff", boxShadow:"0 1px 3px rgba(0,0,0,0.25)", transition:"left 0.25s" }} />
                  </button>
                ) : (
                  <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:99, background:checkSelf(selfSvcIds)?"var(--fc-green-light)":"var(--fc-border-light)", color:checkSelf(selfSvcIds)?"var(--fc-green)":"var(--fc-text-subtle)", border:`1px solid ${checkSelf(selfSvcIds)?"var(--fc-green-border)":"var(--fc-border)"}` }}>
                    {checkSelf(selfSvcIds) ? "On" : "Off"}
                  </span>
                )}
                <span style={{ fontSize:12, color:"var(--fc-text-muted)", fontWeight:500 }}>Self-Service</span>
              </div>
            )}
          </div>

          {/* Right — action buttons */}
          <div style={{ display:"flex", gap:8 }}>
            {isRM && (
              <button onClick={() => setShowRecom(true)} className="fc-btn fc-btn-sm fc-btn-primary fc-btn-pill">
                <i className="fa-solid fa-paper-plane me-1" style={{fontSize:10}} />Recommend
              </button>
            )}
            {isRMOrOwner && (
              <button onClick={() => setTab("proposeFor")} className="fc-btn fc-btn-sm fc-btn-secondary fc-btn-pill">
                <i className="fa-solid fa-briefcase me-1" style={{fontSize:10}} />Propose for Job
              </button>
            )}
            {canEdit && (
              <button onClick={() => navigate(`/resources/edit-resource/${id}`)} className="fc-btn fc-btn-sm fc-btn-ghost fc-btn-pill" style={{ border:"1px solid var(--fc-border)" }}>
                <i className="fa-solid fa-pen me-1" style={{fontSize:10}} />Edit
              </button>
            )}
          </div>
        </div>
      )}

      <ProfileGlance  candidate={candidate} userSlug={userSlug} readOnly={!canEdit} hideIdentity={clientPreview} />
      <ProfileTabs    active={tab} onChange={setTab} tabs={TABS} />

      {tab === "summary"    && <SummaryTab    candidate={candidate} />}
      {tab === "experience" && <ExperienceTab candidate={candidate} />}
      {tab === "education"  && <EducationTab  candidate={candidate} />}
      {tab === "proposeFor" && <ProposeForTab resourceSlug={id} userSlug={userSlug} />}
      {tab === "similar"    && <SimilarTab resourceSlug={id} userSlug={userSlug} candidateName={candidate.name} />}
      {tab === "recommend"  && <RecommendHistoryTab resourceSlug={id} />}
      {tab === "notes"      && <RecruiterNotesTab resourceSlug={id} userSlug={userSlug} />}
      {tab === "connectiq"  && <ConnectIQTab resourceSlug={id} userSlug={userSlug} candidateName={candidate?.name} rd={rd} />}

      {/* ── Modals ── */}
      {showRecom && (
        <RecommendModal
          resourceSlug={id}
          userSlug={userSlug}
          firmId={firmId}
          candidateName={candidate?.name}
          onClose={() => setShowRecom(false)}
        />
      )}
      {showResumeUpdate && (
        <UpdateViaResumeModal
          userSlug={userSlug}
          onClose={() => setShowResumeUpdate(false)}
          onUpdated={() => { setShowResumeUpdate(false); showToast("Profile updated from resume"); loadProfile(); }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:"fixed", bottom:24, right:24, zIndex:9999, background:toast.type==="error"?"var(--fc-red-light)":"#111827", border:toast.type==="error"?"1px solid var(--fc-red-border)":"none", borderRadius:12, padding:"12px 18px", display:"flex", alignItems:"center", gap:10, boxShadow:"0 8px 32px rgba(0,0,0,0.2)" }}>
          <div style={{ width:22, height:22, borderRadius:6, background:toast.type==="error"?"var(--fc-red)":"var(--fc-green)", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <i className={`fa-solid ${toast.type==="error"?"fa-xmark":"fa-check"}`} style={{fontSize:10,color:"#fff"}} />
          </div>
          <span style={{ fontSize:13, fontWeight:600, color:toast.type==="error"?"var(--fc-red)":"#fff" }}>{toast.msg}</span>
        </div>
      )}
      {confirmToggle && (
        <Modal
          title={confirmToggle === "adv" ? (isAdv === "1" ? "Hide from clients?" : "Show to clients?") : "Update self-service access?"}
          onClose={()=>setConfirmToggle(null)}
          maxWidth={400}
          footer={
            <ModalFooter
              onCancel={()=>setConfirmToggle(null)}
              onConfirm={() => {
                const action = confirmToggle === "adv" ? doUpdateAdv : doUpdateSelf;
                setConfirmToggle(null);
                action();
              }}
              confirmLabel={confirmToggle === "adv" ? (isAdv === "1" ? "Hide" : "Show") : "Confirm"}
            />
          }>
          <p style={{ fontSize:13, color:"var(--fc-text-secondary)" }}>
            {confirmToggle === "adv"
              ? `${isAdv === "1" ? "Hide" : "Show"} ${candidate?.name} ${isAdv === "1" ? "from" : "to"} clients?`
              : `Update self-service access for ${candidate?.name}?`}
          </p>
        </Modal>
      )}
    </div>
  );
}